/** Shapes served by /api/catalog/* and /api/onboarding (see docs/api.md and ADR 0031). */

export type FieldClass = 'unclassified' | 'standard' | 'restricted';

export interface CatalogField {
  fieldId: string;
  name: string;
  type: string;
  class: FieldClass;
  description?: string;
  status: 'active' | 'missing';
  needsReview?: boolean;
}

/** A source with its fields, from GET /api/catalog/sources/:id (admin). */
export interface SourceDetail {
  sourceId: string;
  displayName: string;
  description: string;
  summary: string;
  defaultClass: 'standard' | 'restricted';
  enabled: boolean;
  fields: CatalogField[];
}

/** What builders may browse, from GET /api/catalog/available: no field list. */
export interface AvailableSource {
  sourceId: string;
  displayName: string;
  description: string;
  summary: string;
  access: 'standard' | 'restricted';
}

/** What builders may browse of the systems, from GET /api/catalog/available-systems: no tables, no fields. */
export interface AvailableSystem {
  systemId: string;
  displayName: string;
  description: string;
  /** True when any table of the system is by request only. */
  hasRestricted: boolean;
}

/** A table in the catalog list (GET /api/catalog/sources, admin): enough to choose it, no fields. */
export interface TableSummary {
  sourceId: string;
  connector?: string;
  displayName: string;
  summary: string;
  defaultClass: 'standard' | 'restricted';
  enabled: boolean;
}

export interface DataGrant {
  sourceId: string;
  fieldIds: string[];
}

export type LedgerStatus = 'requested' | 'granted' | 'denied' | 'revoked';

export interface LedgerEntry {
  id: string;
  appId: string;
  /** The table. Empty on a system-level entry, which names `systemId` instead. */
  sourceId: string;
  systemId?: string;
  status: LedgerStatus;
  fieldIds: string[];
  reason?: string;
  actor: string;
  at: string;
  requestId?: string;
}

/** A field an admin can put in a grant: active and classified. Hidden and missing ones cannot be granted. */
export const grantableFields = (source: SourceDetail): CatalogField[] =>
  source.fields.filter((f) => f.status === 'active' && f.class !== 'unclassified');

/** Granting any restricted field needs a stated reason. */
export const needsReason = (source: SourceDetail, selected: string[]): boolean => {
  const chosen = new Set(selected);
  return source.fields.some((f) => chosen.has(f.fieldId) && f.class === 'restricted');
};
